import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, 
  FileEdit, 
  Pencil, 
  Trash2, 
  Send, 
  CheckCircle, 
  XCircle, 
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChangeOrders() {
  const { token, user } = useAuth();
  const [changeOrders, setChangeOrders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCO, setEditingCO] = useState(null);
  const [formData, setFormData] = useState({
    jobId: '',
    title: '',
    description: '',
    costImpact: '',
    scheduleImpact: '0',
    reason: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Action dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCO, setDeletingCO] = useState(null);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submittingCO, setSubmittingCO] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approvingCO, setApprovingCO] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingCO, setRejectingCO] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const isOwner = user?.role === 'OWNER';
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchData();
  }, [statusFilter, jobFilter]);

  // Handle "Create from Overage" flow from Job Dashboard
  useEffect(() => {
    if (searchParams.get('createFromOverage') === 'true') {
      const overageData = sessionStorage.getItem('overageCharges');
      if (overageData) {
        try {
          const parsed = JSON.parse(overageData);
          setFormData({
            jobId: parsed.jobId || '',
            title: `Overage - ${parsed.jobNumber || 'Job'}`,
            description: `Change order to cover overage of ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parsed.overageAmount || 0)}.\n\nCharges:\n${(parsed.charges || []).map(c => `- ${c.type}: ${c.description} (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c.amount)})`).join('\n')}`,
            costImpact: (parsed.overageAmount || 0).toString(),
            scheduleImpact: '0',
            reason: 'Budget overage - additional scope required',
            notes: ''
          });
          setDialogOpen(true);
          sessionStorage.removeItem('overageCharges');
        } catch (e) {
          console.error('Failed to parse overage data:', e);
        }
      }
      // Clean up the URL param
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (jobFilter) params.append('job_id', jobFilter);
      
      const [cosRes, jobsRes] = await Promise.all([
        axios.get(`${API}/api/change-orders?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setChangeOrders(cosRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load change orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (co = null) => {
    if (co) {
      setEditingCO(co);
      setFormData({
        jobId: co.jobId,
        title: co.title,
        description: co.description,
        costImpact: co.costImpact.toString(),
        scheduleImpact: co.scheduleImpact?.toString() || '0',
        reason: co.reason || '',
        notes: co.notes || ''
      });
    } else {
      setEditingCO(null);
      setFormData({
        jobId: jobFilter || '',
        title: '',
        description: '',
        costImpact: '',
        scheduleImpact: '0',
        reason: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        costImpact: parseFloat(formData.costImpact),
        scheduleImpact: parseInt(formData.scheduleImpact) || 0
      };

      if (editingCO) {
        await axios.patch(`${API}/api/change-orders/${editingCO.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Change order updated');
      } else {
        await axios.post(`${API}/api/change-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Change order created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save change order:', error);
      toast.error(error.response?.data?.detail || 'Failed to save change order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCO) return;
    try {
      await axios.delete(`${API}/api/change-orders/${deletingCO.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change order deleted');
      setDeleteDialogOpen(false);
      setDeletingCO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmitForApproval = async () => {
    if (!submittingCO) return;
    try {
      await axios.post(`${API}/api/change-orders/${submittingCO.id}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change order submitted for approval');
      setSubmitDialogOpen(false);
      setSubmittingCO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    if (!approvingCO) return;
    try {
      const res = await axios.post(`${API}/api/change-orders/${approvingCO.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Change order approved! New PO Value: $${res.data.newPoValue?.toLocaleString()}`);
      setApproveDialogOpen(false);
      setApprovingCO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectingCO) return;
    try {
      await axios.post(`${API}/api/change-orders/${rejectingCO.id}/reject?reason=${encodeURIComponent(rejectionReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change order rejected');
      setRejectDialogOpen(false);
      setRejectingCO(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
      PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
      APPROVED: 'bg-green-100 text-green-700 border-green-200',
      REJECTED: 'bg-red-100 text-red-700 border-red-200'
    };
    const icons = {
      DRAFT: <FileEdit className="w-3 h-3" />,
      PENDING: <Clock className="w-3 h-3" />,
      APPROVED: <CheckCircle className="w-3 h-3" />,
      REJECTED: <XCircle className="w-3 h-3" />
    };
    return (
      <Badge className={`rounded-sm flex items-center gap-1 ${styles[status] || styles.DRAFT}`}>
        {icons[status]}
        {status}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    const prefix = amount >= 0 ? '+' : '';
    return `${prefix}$${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Summary stats
  const totalApproved = changeOrders.filter(co => co.status === 'APPROVED').reduce((sum, co) => sum + co.costImpact, 0);
  const totalPending = changeOrders.filter(co => co.status === 'PENDING').reduce((sum, co) => sum + co.costImpact, 0);
  const pendingCount = changeOrders.filter(co => co.status === 'PENDING').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A3668]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-4 md:space-y-6" data-testid="change-orders-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-black tracking-tight">Change Orders</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Track contract modifications and PO adjustments</p>
        </div>
        {(isOffice || isOwner) && (
          <Button
            onClick={() => handleOpenDialog()}
            data-testid="create-change-order-btn"
            className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-4 md:py-2 w-full sm:w-auto active:scale-[0.98]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Change Order
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500">Approved</p>
                <p className={`text-lg md:text-2xl font-bold ${totalApproved >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(totalApproved)}
                </p>
              </div>
              <div className="hidden md:block">
                {totalApproved >= 0 ? (
                  <TrendingUp className="w-8 h-8 text-green-200" />
                ) : (
                  <TrendingDown className="w-8 h-8 text-red-200" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500">Pending</p>
                <p className="text-lg md:text-2xl font-bold text-amber-600">{formatCurrency(totalPending)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200 hidden md:block" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500">Awaiting</p>
                <p className="text-lg md:text-2xl font-bold text-[#1A3668]">{pendingCount}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200 hidden md:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200 shadow-sm rounded-sm">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-bold text-slate-600">Filters:</span>
            </div>
            <div className="flex flex-wrap gap-2 flex-1">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-[130px] md:w-40 rounded-sm border-2">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={jobFilter} onValueChange={setJobFilter}>
                <SelectTrigger data-testid="job-filter" className="w-[160px] md:w-60 rounded-sm border-2">
                  <SelectValue placeholder="All Jobs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Jobs</SelectItem>
                  {jobs.map(job => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.jobNumber} - {job.description?.substring(0, 20)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(statusFilter || jobFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStatusFilter(''); setJobFilter(''); }}
                  className="text-slate-500"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Orders List */}
      {changeOrders.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <FileEdit className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No change orders found</p>
            {(isOffice || isOwner) && (
              <Button
                onClick={() => handleOpenDialog()}
                variant="outline"
                className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
              >
                Create your first change order
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {changeOrders.map(co => (
            <Card key={co.id} data-testid={`change-order-${co.id}`} className="border-slate-200 shadow-sm rounded-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-lg font-bold text-[#1A3668]">CO-{String(co.coNumber).padStart(3, '0')}</span>
                      {getStatusBadge(co.status)}
                      <Badge variant="outline" className="rounded-sm text-xs">
                        {co.jobNumber}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{co.title}</h3>
                    <p className="text-slate-600 text-sm mb-2">{co.description}</p>
                    {co.reason && (
                      <p className="text-sm text-slate-500"><strong>Reason:</strong> {co.reason}</p>
                    )}
                    {co.rejectionReason && (
                      <p className="text-sm text-red-600 mt-1"><strong>Rejection Reason:</strong> {co.rejectionReason}</p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
                      <span>Created: {format(new Date(co.createdAt), 'MMM d, yyyy')}</span>
                      {co.submittedAt && <span>Submitted: {format(new Date(co.submittedAt), 'MMM d, yyyy')}</span>}
                      {co.approvedAt && <span className="text-green-600">Approved: {format(new Date(co.approvedAt), 'MMM d, yyyy')}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Cost Impact</p>
                      <p className={`text-2xl font-bold ${co.costImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(co.costImpact)}
                      </p>
                    </div>
                    {co.scheduleImpact !== 0 && (
                      <p className="text-sm text-slate-600">
                        Schedule: {co.scheduleImpact > 0 ? '+' : ''}{co.scheduleImpact} days
                      </p>
                    )}
                    <div className="flex gap-2">
                      {co.status === 'DRAFT' && (isOffice || isOwner) && (
                        <>
                          <Button
                            onClick={() => handleOpenDialog(co)}
                            variant="outline"
                            size="sm"
                            data-testid={`edit-co-${co.id}`}
                            className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            onClick={() => { setSubmittingCO(co); setSubmitDialogOpen(true); }}
                            variant="outline"
                            size="sm"
                            data-testid={`submit-co-${co.id}`}
                            className="border-2 border-blue-200 text-blue-600 hover:bg-blue-50 rounded-sm"
                          >
                            <Send className="w-3 h-3 mr-1" /> Submit
                          </Button>
                          <Button
                            onClick={() => { setDeletingCO(co); setDeleteDialogOpen(true); }}
                            variant="outline"
                            size="sm"
                            data-testid={`delete-co-${co.id}`}
                            className="border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {co.status === 'PENDING' && isOwner && (
                        <>
                          <Button
                            onClick={() => { setApprovingCO(co); setApproveDialogOpen(true); }}
                            size="sm"
                            data-testid={`approve-co-${co.id}`}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button
                            onClick={() => { setRejectingCO(co); setRejectDialogOpen(true); }}
                            variant="outline"
                            size="sm"
                            data-testid={`reject-co-${co.id}`}
                            className="border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCO ? 'Edit Change Order' : 'New Change Order'}
            </DialogTitle>
            <DialogDescription>
              {editingCO ? 'Update change order details' : 'Create a contract modification request'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Job *</Label>
                <Select
                  value={formData.jobId}
                  onValueChange={(value) => setFormData({ ...formData, jobId: value })}
                  disabled={!!editingCO}
                >
                  <SelectTrigger data-testid="co-job-select" className="mt-2 rounded-sm border-2">
                    <SelectValue placeholder="Select job" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map(job => (
                      <SelectItem key={job.id} value={job.id}>
                        {job.jobNumber} - {job.description?.substring(0, 40)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Title *</Label>
                <Input
                  data-testid="co-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief title for the change"
                  required
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Description *</Label>
                <Textarea
                  data-testid="co-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the scope change"
                  required
                  className="mt-2 rounded-sm border-2"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Cost Impact ($) *</Label>
                  <Input
                    data-testid="co-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.costImpact}
                    onChange={(e) => setFormData({ ...formData, costImpact: e.target.value })}
                    placeholder="5000.00 or -2500.00"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Positive = addition, Negative = deduction</p>
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Schedule Impact (Days)</Label>
                  <Input
                    data-testid="co-schedule-input"
                    type="number"
                    value={formData.scheduleImpact}
                    onChange={(e) => setFormData({ ...formData, scheduleImpact: e.target.value })}
                    placeholder="0"
                    className="mt-2 rounded-sm border-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Days added/removed from schedule</p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Reason for Change</Label>
                <Input
                  data-testid="co-reason-input"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Client request, unforeseen conditions, etc."
                  className="mt-2 rounded-sm border-2"
                />
              </div>
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                <Textarea
                  data-testid="co-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes or references"
                  className="mt-2 rounded-sm border-2"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-co-btn"
                disabled={submitting || !formData.jobId || !formData.title || !formData.costImpact}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {submitting ? 'Saving...' : editingCO ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Change Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>CO-{String(deletingCO?.coNumber).padStart(3, '0')}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-sm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Confirmation */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit for Approval</AlertDialogTitle>
            <AlertDialogDescription>
              Submit <strong>CO-{String(submittingCO?.coNumber).padStart(3, '0')}</strong> for owner approval?
              <br /><br />
              <strong>Cost Impact:</strong> {submittingCO && formatCurrency(submittingCO.costImpact)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmitForApproval} className="bg-blue-600 hover:bg-blue-700 rounded-sm">
              Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve Confirmation */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Change Order</AlertDialogTitle>
            <AlertDialogDescription>
              Approve <strong>CO-{String(approvingCO?.coNumber).padStart(3, '0')}</strong>?
              <br /><br />
              This will adjust the job's PO value by <strong>{approvingCO && formatCurrency(approvingCO.costImpact)}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} className="bg-green-600 hover:bg-green-700 rounded-sm">
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-[400px] rounded-sm">
          <DialogHeader>
            <DialogTitle>Reject Change Order</DialogTitle>
            <DialogDescription>
              Reject CO-{String(rejectingCO?.coNumber).padStart(3, '0')}? Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Rejection Reason</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="mt-2 rounded-sm border-2"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
